import { BehaviorSubject, map, Observable, of } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { catchError, shareReplay } from 'rxjs/operators';

// services
import { InputsService } from '@app/inputs/services/inputs.service';
import { InputsFacadeBaseV2 } from '@app/user-content/user-input-v2/services/inputs-facade-base';
import { BookMapper } from './book-mapper.service';
import { GoogleBooksService } from '@dg/shared-services';
import { InputImageUploadAdapterService } from '@app/uploader/upload-section/adapters/input-image-upload-adapter.service';
import { BookService } from './book.service';

// misc
import { InputContext } from '@app/user-content/user-input-v2/input.model';
import { SubmissionStatus } from '@app/inputs/inputs.model';
import { Book } from '@app/inputs/inputs-api.model';
import { TypeaheadSearchFunction } from '@app/shared/shared-api.model';
import { lazySearch } from '@dg/shared-rxjs';
import { ViewModel } from '../book.model';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { markFormAsTouched } from '@app/shared/utils/angular-form-field-helpers';
import { pagesLabel } from '../services/book.utils';
/**
 * Base Book service to define shared functionality, with the ability to overwrite if it is different.
 */
@Injectable()
export abstract class BookFacadeBase extends InputsFacadeBaseV2<ViewModel> {
  public override viewModel$ = new BehaviorSubject<ViewModel>(undefined);
  public activeModal: NgbActiveModal = inject(NgbActiveModal);
  public bookMapper: BookMapper = inject(BookMapper);
  public bookService: BookService = inject(BookService);
  public googleBooksService: GoogleBooksService = inject(GoogleBooksService);

  protected constructor(
    public override inputsService: InputsService,
    protected override inputImageUploadAdapterService: InputImageUploadAdapterService
  ) {
    super(inputsService, inputImageUploadAdapterService);
  }

  /**
   * Overwrite the on Next form the base facade for catalog
   * When the next button has been selected
   * @param url
   * @returns
   */
  public onNext(form: FormGroup): Observable<ViewModel> {
    markFormAsTouched(form);
    if (form.invalid) {
      return of(null);
    }
    const i18n = {
      ...this.viewModel.i18n,
    };
    const lengthLabel = pagesLabel(
      this.viewModel?.book?.durationUnitType,
      this.translateService
    );
    // Update the view model with the book selected
    return super
      .patchViewModel({
        ...i18n,
        book: form?.get('book').value,
        isInitialForm: false,
        lengthLabel,
      })
      .pipe(shareReplay({ refCount: false, bufferSize: 1 }));
  }

  public onSubmit(form: FormGroup, isUserMediaEntry = false): void {
    markFormAsTouched(form);
    if (form.invalid) {
      return;
    }
    const formData = form.value;
    // Step 1 update the View model with the form data
    this.updateViewWithFormData(formData);
    // step 2 get API Params mapper
    const apiParameters = this.bookMapper.toApiParameters(
      this.viewModel as any
    );

    // TODO: adding in placeholder for global add book content
    const addMedia$ = !isUserMediaEntry
      ? this.bookService.addBook(apiParameters as any)
      : this.bookService.addUserBook(apiParameters as any);
    this.manageSubmitStatus(addMedia$).subscribe({
      next: (submissionResult: any) => {
        super.patchViewModel({
          inputId: submissionResult.result?.inputId ?? this.viewModel.inputId,
          submissionResult,
        });

        this.activeModal.close(this.viewModel);
      },
    });
  }

  /**
   * Book helpers
   */

  /**
   * On select book, update the form with the book selected
   * @param form
   * @param book
   */
  public onSelectBook = (form: FormGroup, book: Partial<Book>) => {
    form.get('book').setValue(book);

    this.viewModel.book = book as Book;
    this.viewModel.completionInfo.length = book.durationUnits;
  };

  /**
   * Search for a book title
   * @param form
   * @returns
   */
  public searchTitle(form: FormGroup): TypeaheadSearchFunction<string, any> {
    return (term$: Observable<string>): Observable<readonly any[]> => {
      return term$.pipe(
        lazySearch((term) => {
          if (term.length >= 2) {
            return this.googleBooksService.find(term).pipe(
              map((books) => {
                form.controls['book'].setErrors(null);

                return books.map((book) => ({
                  ...book,
                  altBookCover: this.generateAltCover(book.title),
                }));
              }),
              catchError((error) => {
                form.controls['book'].setErrors({ bookNotFound: true });
                form.controls['book'].markAsTouched();
                return of([]);
              })
            );
          }

          return of([]);
        })
      );
    };
  }

  public getBookTitleError(form: FormGroup): string {
    return form.get('book').hasError('bookNotFound')
      ? this.viewModel.i18n.BookFormCtrl_BookSearchError
      : this.viewModel.i18n.MediaFormCtrl_TitleRequired;
  }

  /********************************************************
   * View Model
   ********************************************************/

  protected initializeViewModel(
    inputContext: InputContext
  ): Observable<ViewModel> {
    // initialize context
    // Listen for changes on submission Status
    const shouldSpinSubmitButton$ = this.submissionStatus$.pipe(
      map((status) => status === SubmissionStatus.Submitting)
    );
    super.initializeContextViewModel(inputContext);
    // initialize new/computed Properties

    const API = {
      onSelectBook: this.onSelectBook.bind(this),
      formatBookResult: this.formatBookResult.bind(this),
      onFormInitialInput: this.onFormInitialInput.bind(this),
      getBookTitleError: this.getBookTitleError.bind(this),
    };
    // Base i18n strings shared across child facades
    const i18n = this.translateService.instant([
      'Core_MoreInfo',
      'Core_Next',
      'Core_Required',
      'Core_Skills',
      'Core_Title',
      'MediaFormCtrl_TitleRequired',
      'dgOrgInternalContent_SkillsTooltipText',
      'dgOrgInternalContent_SkillsPlaceholderText',
      'dgOrgInternalContent_SkillsMaxError',
      'BookFormCtrl_BookTitle',
      'BookFormCtrl_SaveBook',
      'BookFormCtrl_BookSearchError',
      'BookFormCtrl_Length',
      'dgOrgInternalContentForm_BookLengthCustomFormat',
      'BookFormCtrl_BookLength',
      'BookFormCtrl_AddBook',
      'BookFormCtrl_EditBook',
    ]);

    // Can only add not edit books
    const modalHeader = i18n.BookFormCtrl_AddBook;

    const initialVM = {
      ...this.viewModel,
      book: undefined,
      completionInfo: {
        length: undefined,
        dateRead: undefined,
      },
      bookSearchTitle: '',
      tags: [],
    };

    return super
      .patchViewModel({
        i18n,
        shouldSpinSubmitButton$,
        modalHeader,
        ...API,
        ...initialVM,
      })
      .pipe(shareReplay({ refCount: false, bufferSize: 1 }));
  }

  /**
   * Update the view with the current form data values
   * @param formData
   */
  protected updateViewWithFormData(formData: any): void {
    super
      .patchViewModel({
        tags: formData.skills,
        completionInfo: {
          length: formData.length,
          dateRead: formData.dateRead,
        },
        ...formData,
      })
      .pipe(shareReplay({ refCount: false, bufferSize: 1 }));
  }

  protected onFormInitialInput(value: string) {
    if (value.length === 0) {
      this.viewModel.bookSearchTitle = '';
      return;
    }

    this.viewModel.bookSearchTitle = value;
  }

  private formatBookResult(book: Book): string {
    return book.title;
  }

  /**
   * Generate an alternative text for the cover image for the book
   * @param title 
   * @returns 
   */
  private generateAltCover(title: string): string {
    return this.translateService.instant('BookFormCtrl_BookCoverAltFormat', {
      title: title,
    });
  }
}
